import { TooltipProvider } from "@/components/ui/tooltip";
import { Eraser, Highlighter, Pen } from "lucide-react";
import { useEffect } from "react";
import { useCanvasToolbarLayout } from "../context/CanvasToolbarLayoutContext";
import { useCanvasDrawStyle } from "../hooks/useCanvasDrawStyle";
import { EDGE_COLORS } from "../nodes/shared/CanvasColorPalettes";
import { canvasSwatchColors } from "../nodes/shared/CanvasColorPickerButton";
import {
  CanvasStrokeSquigglePreview,
  CanvasToolbarColorSwatch,
} from "../nodes/shared/CanvasToolbarGraphics";
import { CANVAS_STROKE_WIDTHS } from "../types/canvasNodeData";
import { DRAWING_TIPS, type DrawingTip } from "../utils/drawingTips";
import {
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarGroup,
  CanvasToolbarShell,
} from "./toolbar";

const TIP_ICONS: Record<DrawingTip, typeof Pen> = {
  pen: Pen,
  highlighter: Highlighter,
};

const WIDTH_LABELS: Record<number, string> = {
  1: "Thin",
  2: "Medium",
  4: "Heavy",
};

interface DrawPopoverProps {
  /** Called when Escape is pressed. */
  onClose: () => void;
}

/**
 * Pen options — tip, weight, colour on one row, every option a single click.
 * Same shell and button sizing as the primary toolbar.
 */
export function DrawPopover({ onClose }: DrawPopoverProps) {
  const { flyoutOrientation, tooltipSide } = useCanvasToolbarLayout();
  const { drawTip, drawColor, drawStrokeWidth, drawErasing, setDrawStyle } =
    useCanvasDrawStyle();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <TooltipProvider>
      <CanvasToolbarShell orientation={flyoutOrientation} aria-label="Draw">
        <CanvasToolbarGroup orientation={flyoutOrientation}>
          {DRAWING_TIPS.map((option) => {
            const Icon = TIP_ICONS[option.key];
            return (
              <CanvasToolbarButton
                key={option.key}
                label={option.name}
                tooltipSide={tooltipSide}
                active={!drawErasing && drawTip === option.key}
                onClick={() => setDrawStyle({ tip: option.key })}
              >
                <Icon className="size-4" strokeWidth={2} />
              </CanvasToolbarButton>
            );
          })}

          <CanvasToolbarButton
            label="Eraser"
            tooltipSide={tooltipSide}
            active={drawErasing}
            onClick={() => setDrawStyle({ erasing: true })}
          >
            <Eraser className="size-4" strokeWidth={2} />
          </CanvasToolbarButton>
        </CanvasToolbarGroup>

        <CanvasToolbarDivider orientation={flyoutOrientation} />

        <CanvasToolbarGroup orientation={flyoutOrientation}>
          {CANVAS_STROKE_WIDTHS.map((width) => (
            <CanvasToolbarButton
              key={width}
              label={WIDTH_LABELS[width]}
              tooltipSide={tooltipSide}
              active={drawStrokeWidth === width}
              onClick={() => setDrawStyle({ strokeWidth: width })}
            >
              <CanvasStrokeSquigglePreview
                width={width}
                active={drawStrokeWidth === width}
              />
            </CanvasToolbarButton>
          ))}
        </CanvasToolbarGroup>

        <CanvasToolbarDivider orientation={flyoutOrientation} />

        <CanvasToolbarGroup orientation={flyoutOrientation}>
          {EDGE_COLORS.map((color) => {
            const { fill, stroke } = canvasSwatchColors(color);
            return (
              <CanvasToolbarButton
                key={color.value}
                label={color.name}
                tooltipSide={tooltipSide}
                active={drawColor === color.swatchHex}
                onClick={() => setDrawStyle({ color: color.swatchHex })}
              >
                <CanvasToolbarColorSwatch fill={fill} stroke={stroke} />
              </CanvasToolbarButton>
            );
          })}
        </CanvasToolbarGroup>
      </CanvasToolbarShell>
    </TooltipProvider>
  );
}
